import { useEffect, useState } from 'react';
import { go } from './nav.js';
import { getToken } from './api.js';
import { holdPending, keepPending, dropPending } from './pending.js';
import { storeLink } from './getapp.jsx';

/* ══════════════════════════════════════════════════════════════════════════
 * WHAT NEEDS AN ELEATER ACCOUNT — AND THE ONE SHEET THAT ASKS FOR IT.
 *
 * Al, 30 September 2026: *"visitors should not have to sign up at all. They
 * should just be able to go in… if you are looking at reserving, putting a
 * heart, putting a flag, writing a review, in the feeds and things like that
 * then it'll prompt you to either sign in or become an eleater at that time.
 * So it's not right away."*
 *
 * ⚠️ NOTHING HERE IS NEW COPY. The sheet, its heading, its sentence and both
 * buttons are `Eleater Prototype/eleat_gate.js` word for word. The four
 * reasons are that file's own `why` strings — reserve and review from the
 * current file, save and like from the 9 September copy of it, which is the
 * last time those two actions were gated and so the last time Eleat had words
 * for them.
 *
 * ⚠️ THE GATE NEVER LOSES THE ACTION — the locked file's rule: *"A gate that
 * dumps someone on Home has not deferred the action, it has destroyed it."*
 * So Continue carries `return=` (where they were) into the Welcome door, and
 * every door forwards it: Welcome → Join or Sign in → back to that exact spot.
 * Before this, every one of these taps went to `?signin=1` and signing in
 * landed on Home, so somebody who hearted a restaurant never saw it again.
 * ══════════════════════════════════════════════════════════════════════════ */

export const WHY = {
  reserve: 'to reserve a table',
  review: 'to write a review',
  save: 'to save this restaurant',
  like: 'to like this post',
  share: 'to share this',
  /* Al-Noor, 22 Oct 2026: the microphone and her voice are for Eleaters;
     a visitor types 3 questions a day (new words — his to change) */
  talk: 'to talk to Zaryna',
  ask: 'to keep asking Zaryna',
  browse: 'to save the places you like',
};

/* ⚠️ A RETURN IS ONLY EVER A QUERY STRING ON THIS PAGE. Anything not starting
   with `?` is refused, so `return=` can never send somebody to another site
   after they have just typed a password — the open-redirect every sign-in page
   has to close. And never back INTO a door: returning to the sign-in form
   after signing in is a loop. */
const DOORS = ['welcome', 'signin', 'join', 'reset'];

export function safeReturn(r) {
  if (typeof r !== 'string' || !r.startsWith('?') || r.length > 500) return '?';
  const p = new URLSearchParams(r);
  if (DOORS.some((k) => p.has(k))) return '?';
  return r;
}

/* where this door was asked to send them afterwards — Home when nobody said */
export function returnParam() {
  return safeReturn(new URLSearchParams(window.location.search).get('return') || '');
}

/* the address of a door, with the way back attached when there is one */
export function withReturn(door, ret) {
  const r = safeReturn(ret);
  return r === '?' ? door : `${door}&return=${encodeURIComponent(r)}`;
}

export function hereNow() {
  return window.location.search || '?';
}

/* ⚠️ THE ONE QUESTION EVERY GATED CONTROL ASKS. Signed in → false, carry on.
   Signed out → the sheet opens and the caller stops. One function, so the
   gate cannot end up on four controls and missing from a fifth. */
/* `then` is what to do once they are in — { save: id } · { like: feedId } ·
   { review: venueId } · { ret: '?reserve=…' } (where to land) — so signing
   in finishes the tap instead of forgetting it (pending.js). */
export function needsAccount(intent, then) {
  if (getToken()) return false;
  holdPending(then);
  window.dispatchEvent(new CustomEvent('eleat:gate', { detail: { intent, ret: then && then.ret } }));
  return true;
}

/* ⚠️ A NUDGE, NOT A WALL — Al-Noor, 22 Oct 2026 ("after some time, we should
   get them to sign up"): a visitor who has opened 5 restaurant pages in this
   visit sees the sheet once; "Not now" closes it and it does not come back
   that day. Browsing never stops (Google ranks covered pages lower, and the
   restaurants pay to be seen). */
let waiting = null;
export function countVenueView() {
  if (getToken()) return;
  try {
    const n = Number(sessionStorage.getItem('eleat.venueViews') || 0) + 1;
    sessionStorage.setItem('eleat.venueViews', String(n));
    const today = new Date().toDateString();
    if (n >= 5 && localStorage.getItem('eleat.joinNudge') !== today) {
      localStorage.setItem('eleat.joinNudge', today);
      /* kept until the sheet is listening: on a fresh page the sheet is drawn
         after the restaurant page (found walking it, 22 Oct) */
      waiting = { intent: 'browse', ret: null };
      window.dispatchEvent(new CustomEvent('eleat:gate', { detail: waiting }));
    }
  } catch { /* private window: no nudge */ }
}

/* ⚠️ "NOT NOW" LEAVES THEM EXACTLY WHERE THEY WERE, STILL BROWSING — the
   locked file: *"Not a wall and not a nag: one short sheet naming what they
   were doing, with the way out as prominent as the way on."* */
export function JoinSheet() {
  const [intent, setIntent] = useState(null);
  const [ret, setRet] = useState(null);

  useEffect(() => {
    const open = (e) => { waiting = null; setIntent((e.detail && e.detail.intent) || null); setRet((e.detail && e.detail.ret) || null); };
    window.addEventListener('eleat:gate', open);
    if (waiting) open({ detail: waiting });
    return () => window.removeEventListener('eleat:gate', open);
  }, []);

  useEffect(() => {
    if (!intent) return undefined;
    const esc = (e) => { if (e.key === 'Escape') { dropPending(); setIntent(null); } };
    window.addEventListener('keydown', esc);
    return () => window.removeEventListener('keydown', esc);
  }, [intent]);

  if (!intent || !WHY[intent]) return null;

  return (
    <div className="egate" role="dialog" aria-modal="true" aria-labelledby="egateH">
      <div className="egate-veil" onClick={() => { dropPending(); setIntent(null); }} />
      <div className="egate-card">
        <div className="egate-grip" />
        {/* ⚠️ Profile's own words (`08_Profile.html #pfGuest`) — Al, 6 Oct 2026:
            "sign in or join eleat on profile is correct… no consistency". An
            Eleater who is signed out is asked to sign in, not to become one. */}
        <h3 className="egate-h" id="egateH">Sign in or join Eleat</h3>
        <p className="egate-s">
          Eleat asks for an account {WHY[intent]}. Browsing stays open — this is
          only so it is yours, and so it is here next time.
        </p>
        {/* on a phone, once Eleat is in the stores: the app is the main way on
            (Al-Noor, 22 Oct 2026 — "push them onto the app side") */}
        {storeLink() && <a className="egate-go" href={storeLink()} rel="noopener">Get the Eleat app</a>}
        <button className={storeLink() ? 'egate-no egate-web' : 'egate-go'} type="button"
          onClick={() => { keepPending(); setIntent(null); go(withReturn('?welcome=1', ret || hereNow())); }}>
          {storeLink() ? 'Continue on the web' : 'Continue'}
        </button>
        <button className="egate-no" type="button" onClick={() => { dropPending(); setIntent(null); }}>
          Not now
        </button>
      </div>
    </div>
  );
}
